import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { portalApi } from '../api/portalApi';

// Re-export RTK Query hooks for direct component use
export {
  useGetUserPreferencesQuery,
  useUpdateUserPreferenceMutation,
  useCreateUserPreferenceMutation,
  useDeleteUserPreferenceMutation,
} from '../api/portalApi';

/**
 * Backward-compatible wrapper for fetchUserPermissions.
 * Fetches all user preferences via RTK Query and filters client-side.
 */
export const fetchUserPermissions = createAsyncThunk(
  'permissions/fetchUserPermissions',
  async (userObjectId, { dispatch, rejectWithValue }) => {
    try {
      const normalizedUserObjectId = String(userObjectId || '').toLowerCase();
      const result = await dispatch(
        portalApi.endpoints.getUserPreferences.initiate(undefined, {
          forceRefetch: true,
        })
      );

      if (result.error) {
        return rejectWithValue(result.error.data || 'Failed to fetch user preferences');
      }

      const data = result.data || [];
      const permissions = {};
      data
        .filter((pref) => {
          const category = pref.Category || pref.category;
          const preferenceUserObjectId = String(
            pref.UserObjectID || pref.userObjectID || pref.userObjectId || ''
          ).toLowerCase();

          if (category !== 'ComponentPermissions') {
            return false;
          }

          if (!normalizedUserObjectId) {
            return true;
          }

          return preferenceUserObjectId === normalizedUserObjectId;
        })
        .forEach(pref => {
          const key = pref.Key || pref.key;
          const value = pref.Value || pref.value;

          if (key) {
            permissions[key] = value;
          }
        });

      return permissions;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Backward-compatible wrapper for fetchPermissionTemplates.
 */
export const fetchPermissionTemplates = createAsyncThunk(
  'permissions/fetchPermissionTemplates',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        portalApi.endpoints.getUserPreferences.initiate(undefined, {
          forceRefetch: false,
        })
      );

      if (result.error) {
        return rejectWithValue(result.error.data || 'Failed to fetch permission templates');
      }

      const data = result.data || [];
      const templates = {};
      data
        .filter(template => template.Category === 'PermissionTemplate')
        .forEach(template => {
          try {
            templates[template.Key] = {
              name: template.Key,
              description: JSON.parse(template.Value).description || '',
              permissions: JSON.parse(template.Value).permissions || {}
            };
          } catch (parseError) {
            console.warn(`Failed to parse template ${template.Key}:`, parseError);
          }
        });

      return templates;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Backward-compatible wrapper for saveUserPermissions.
 * Creates one user-preference record per permission entry via RTK Query.
 */
export const saveUserPermissions = createAsyncThunk(
  'permissions/saveUserPermissions',
  async ({ userObjectId, permissions }, { dispatch, rejectWithValue }) => {
    try {
      const requests = Object.entries(permissions).map(([componentKey, permission]) =>
        dispatch(
          portalApi.endpoints.createUserPreference.initiate({
            UserObjectID: userObjectId,
            Category: 'ComponentPermissions',
            Key: componentKey,
            Value: permission,
          })
        )
      );

      await Promise.all(requests);
      return { userObjectId, permissions };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Backward-compatible wrapper for savePermissionTemplate.
 */
export const savePermissionTemplate = createAsyncThunk(
  'permissions/savePermissionTemplate',
  async ({ templateName, description, permissions }, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        portalApi.endpoints.createUserPreference.initiate({
          userObjectID: 'system-templates',
          Category: 'PermissionTemplate',
          Key: templateName,
          Value: JSON.stringify({ description, permissions }),
        })
      );

      if (result.error) {
        return rejectWithValue(result.error.data || 'Failed to save permission template');
      }

      return { templateName, description, permissions };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState: {
    // User-specific permissions
    userPermissions: {},
    userPermissionsStatus: 'idle',
    userPermissionsError: null,
    
    // Permission templates
    templates: {},
    templatesStatus: 'idle',
    templatesError: null,
    
    // Cache for computed permissions
    computedPermissions: {},
    
    // Loading states
    savingPermissions: false,
    savingTemplate: false
  },
  reducers: {
    replaceRealtimePermissionTemplates: (state, action) => {
      const templates = {};
      for (const row of action.payload) {
        if ((row.Category || row.category) !== 'PermissionTemplate') continue;
        const name = row.Key || row.key;
        if (!name) continue;
        let value;
        try { value = JSON.parse(row.Value || row.value); } catch { continue; }
        templates[name] = { name, description: value.description || '', permissions: value.permissions || {} };
      }
      state.templates = templates;
      state.templatesStatus = 'succeeded';
    },
    replaceRealtimeUserPermissions: (state, action) => {
      const { rows, userObjectId } = action.payload;
      const userId = String(userObjectId || '').toLowerCase();
      if (!userId) return;
      state.userPermissions = Object.fromEntries(rows.filter(pref =>
        (pref.Category || pref.category) === 'ComponentPermissions' &&
        String(pref.UserObjectID || pref.userObjectID || pref.userObjectId || '').toLowerCase() === userId
      ).map(pref => [pref.Key || pref.key, pref.Value || pref.value]).filter(([key]) => Boolean(key)));
      state.userPermissionsStatus = 'succeeded';
    },
    // Clear user permissions (for logout)
    clearUserPermissions: (state) => {
      state.userPermissions = {};
      state.computedPermissions = {};
    },
    
    // Set computed permissions (called by permission engine)
    setComputedPermissions: (state, action) => {
      state.computedPermissions = action.payload;
    },
    
    // Update single permission temporarily (for UI updates)
    updatePermissionLocally: (state, action) => {
      const { componentKey, permission } = action.payload;
      state.userPermissions[componentKey] = permission;
    },
    
    // Clear computed permissions cache
    clearComputedPermissions: (state) => {
      state.computedPermissions = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user permissions
      .addCase(fetchUserPermissions.pending, (state) => {
        state.userPermissionsStatus = 'loading';
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.userPermissionsStatus = 'succeeded';
        state.userPermissions = action.payload;
        state.userPermissionsError = null;
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.userPermissionsStatus = 'failed';
        state.userPermissionsError = action.payload;
      })
      
      // Fetch permission templates
      .addCase(fetchPermissionTemplates.pending, (state) => {
        state.templatesStatus = 'loading';
      })
      .addCase(fetchPermissionTemplates.fulfilled, (state, action) => {
        state.templatesStatus = 'succeeded';
        state.templates = action.payload;
        state.templatesError = null;
      })
      .addCase(fetchPermissionTemplates.rejected, (state, action) => {
        state.templatesStatus = 'failed';
        state.templatesError = action.payload;
      })
      
      // Save user permissions
      .addCase(saveUserPermissions.pending, (state) => {
        state.savingPermissions = true;
      })
      .addCase(saveUserPermissions.fulfilled, (state, action) => {
        state.savingPermissions = false;
        state.userPermissions = action.payload.permissions;
      })
      .addCase(saveUserPermissions.rejected, (state, action) => {
        state.savingPermissions = false;
        state.userPermissionsError = action.payload;
      })
      
      // Save permission template
      .addCase(savePermissionTemplate.pending, (state) => {
        state.savingTemplate = true;
      })
      .addCase(savePermissionTemplate.fulfilled, (state, action) => {
        state.savingTemplate = false;
        const { templateName, description, permissions } = action.payload;
        state.templates[templateName] = {
          name: templateName,
          description,
          permissions
        };
      })
      .addCase(savePermissionTemplate.rejected, (state, action) => {
        state.savingTemplate = false;
        state.templatesError = action.payload;
      });
  }
});

// Export actions
export const {
  clearUserPermissions,
  setComputedPermissions,
  updatePermissionLocally,
  clearComputedPermissions
} = permissionsSlice.actions;

// Selectors
export const selectUserPermissions = (state) => state.permissions.userPermissions;
export const selectPermissionTemplates = (state) => state.permissions.templates;
export const selectComputedPermissions = (state) => state.permissions.computedPermissions;
export const selectUserPermissionsStatus = (state) => state.permissions.userPermissionsStatus;
export const selectTemplatesStatus = (state) => state.permissions.templatesStatus;
export const selectSavingPermissions = (state) => state.permissions.savingPermissions;
export const selectSavingTemplate = (state) => state.permissions.savingTemplate;
export const selectPermissionsError = (state) => state.permissions.userPermissionsError;
export const selectTemplatesError = (state) => state.permissions.templatesError;

export default permissionsSlice.reducer;

export const { replaceRealtimeUserPermissions, replaceRealtimePermissionTemplates } = permissionsSlice.actions;
